import { getToken, onMessage } from 'firebase/messaging';

import env from 'config/env';
import { getMessagingIfSupported } from 'services/firebase';
import { notificationService } from 'services/notificationService';

const SW_URL = '/firebase-messaging-sw.js';

/**
 * Build the service-worker URL with the Firebase config appended as query
 * params. Files under `public/` are copied verbatim by CRA (webpack never
 * inlines `process.env` there), so the SW cannot read the env directly — the
 * app forwards the config through its registration URL instead, and the SW
 * reads it from `self.location.search`.
 */
function buildSwUrl() {
  const params = new URLSearchParams(env.firebaseConfig);
  return `${SW_URL}?${params.toString()}`;
}

// De-dupe concurrent calls (e.g. React StrictMode double-mount). Cleared on
// logout so a fresh login can register again in the same tab.
let registerPromise = null;

/** Lightweight pub/sub so the bell can refresh when a push arrives in foreground. */
const pushListeners = new Set();

export function onPushNotification(listener) {
  pushListeners.add(listener);
  return () => pushListeners.delete(listener);
}

function emitPushReceived(payload) {
  pushListeners.forEach((fn) => {
    try {
      fn(payload);
    } catch {
      /* listener errors must not break push handling */
    }
  });
}

/** Call on logout so the next sign-in can POST /notifications/fcm-token again. */
export function resetPushNotifications() {
  registerPromise = null;
}

/**
 * Register THIS browser for Firebase Cloud Messaging web-push (best-effort):
 *   1. bail if the browser can't do web-push, or no VAPID key is configured,
 *   2. register the messaging service worker,
 *   3. request notification permission + fetch the FCM token,
 *   4. hand the token to the backend (stored on the current session, see
 *      POST /notifications/fcm-token).
 *
 * Never throws — push is additive; the in-app bell works regardless. Only call
 * this for an authenticated user (the token is saved against their session).
 */
export async function initPushNotifications() {
  if (registerPromise) return registerPromise;

  registerPromise = (async () => {
    if (!('serviceWorker' in navigator) || !('Notification' in window)) return;

    if (!env.firebaseVapidKey) {
      // eslint-disable-next-line no-console
      console.info('[push] REACT_APP_FIREBASE_VAPID_KEY not set — web-push disabled (in-app bell still works).');
      return;
    }

    const messaging = await getMessagingIfSupported();
    if (!messaging) return;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      // eslint-disable-next-line no-console
      console.info('[push] notification permission not granted — FCM token not sent to server.');
      return;
    }

    const registration = await navigator.serviceWorker.register(buildSwUrl());
    const token = await getToken(messaging, {
      vapidKey: env.firebaseVapidKey,
      serviceWorkerRegistration: registration,
    });
    if (!token) return;

    await notificationService.saveFcmToken(token);

    onMessage(messaging, (payload) => {
      emitPushReceived(payload);
      // Pushes are data-only (the service worker explains why), so title/body
      // live in `data`; `notification` is only read as a fallback for a push
      // that was queued by an older backend.
      const data = payload?.data || {};
      const title = data.title || payload?.notification?.title;
      const body = data.body || payload?.notification?.body || '';
      // Chat pushes are noisy while you're actively looking at the app — the
      // in-app UI already updates live — so only pop an OS notification for a
      // chat message when the tab is hidden. Covers every chat-type push
      // (`chat`, `chat_message`, `chat_mention`). Other notifications always show.
      const isChat = (data.type || '').startsWith('chat');
      if (isChat && document.visibilityState === 'visible') return;
      if (title && Notification.permission === 'granted') {
        try {
          // The bell announces this same row over the WebSocket under the same
          // tag; matching tags collapse the two copies into one popup.
          const tag = data.tag || data.notification_id || undefined;
          const osNote = new Notification(title, {
            body,
            icon: '/logo192.png',
            tag,
            renotify: Boolean(tag),
            data,
          });
          // Clicking a foreground notification must redirect to its deep link
          // (chat conversation / to-do) — previously it did nothing.
          osNote.onclick = () => {
            const link = data.link_url;
            window.focus();
            if (link) window.location.assign(link);
            osNote.close();
          };
        } catch {
          /* some browsers only allow SW-shown notifications — safe to ignore */
        }
      }
    });
  })().catch((err) => {
    registerPromise = null;
    // eslint-disable-next-line no-console
    console.warn('[push] registration skipped:', err?.message || err);
  });

  return registerPromise;
}

export default initPushNotifications;
